'use client'

import { useEffect, useId, useRef, useState } from 'react'

import { cx } from '@/app/lib/utils'
import { IconChevDown, IconChevUp } from '@/app/components/atoms/Icons'
import { RangeSliderField, defaultFormatValue } from '@/app/components/shared/forms/RangeSliderField'

import { isFreeOnlyRangeValue } from './buildActiveFilterChips'
import styles from './RangeDropdownField.module.scss'
import type { ListingRangeField } from './types'

/**
 * Desplegable del campo "rango" (Precio en Promociones): mismo patrón visual
 * e interactivo que `SelectField` (disparador `position: relative` + panel
 * `position: absolute`, sin floating-ui ni Popover API), pero el panel no es
 * un `listbox` de opciones discretas — contiene un `RangeSliderField` (+ el
 * `extra` que traiga, p. ej. el checkbox "Mostrar solo gratuitos"). Por eso
 * el trigger usa `aria-haspopup="dialog"` (no `"listbox"`) y el panel
 * `role="group"` (no `role="dialog"`: no implementamos foco atrapado ni
 * modalidad, así que no se anuncia como tal).
 *
 * A diferencia de `SelectField`, el panel NO se autocierra tras
 * `onChangeEnd` — un rango continuo puede requerir varios ajustes seguidos
 * de ambas asas antes de que el usuario decida cerrar.
 */
export function RangeDropdownField({
  rangeField,
  variant,
}: {
  rangeField: ListingRangeField
  variant?: 'inverse'
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const panelId = `${useId()}-range-panel`

  const formatValue = rangeField.formatValue ?? defaultFormatValue
  const isDefaultRange = rangeField.value.min === rangeField.min && rangeField.value.max === rangeField.max
  // Caso "Mostrar solo gratuitos" (`[0, 0]`): el disparador anuncia el filtro
  // ("Gratuitos") en vez del rango numérico "0 €–0 €" — mismo criterio que el
  // chip de "filtro aplicado" (`buildActiveFilterChips`).
  const displayText = isDefaultRange
    ? undefined
    : isFreeOnlyRangeValue(rangeField)
      ? 'Gratuitos'
      : `${formatValue(rangeField.value.min)} - ${formatValue(rangeField.value.max)}`

  // Foco al abrir: la primera asa del slider — el panel todavía no existe en
  // el render donde se pulsa el disparador, así que es un efecto de DOM puro.
  useEffect(() => {
    if (isOpen) panelRef.current?.querySelector<HTMLInputElement>('input[type="range"]')?.focus()
  }, [isOpen])

  // Cierra al hacer click fuera o al mover el foco fuera del contenedor —
  // idéntico a `SelectField`.
  useEffect(() => {
    if (!isOpen) return undefined
    const container = containerRef.current

    const handleOutside = (event: MouseEvent) => {
      if (!container?.contains(event.target as Node)) close(false)
    }
    const handleFocusOut = (event: FocusEvent) => {
      // `relatedTarget` es `null` cuando el foco no aterriza en ningún
      // elemento concreto — no solo al perder el foco genuinamente hacia
      // fuera, sino también cuando el propio elemento enfocado se
      // deshabilita en el mismo gesto (p. ej. el slider al marcar "Mostrar
      // solo gratuitos": `rangeField.sliderDisabled` pasa a `true` y el
      // navegador fuerza un blur sin `relatedTarget`). Tratar ese `null`
      // como "el foco salió del desplegable" cerraba el panel con
      // cualquier interacción interna (bug reportado). El cierre por click
      // fuera ya lo cubre `handleOutside` (mousedown); este handler solo
      // hace falta para el Tab del teclado, que sí entrega un
      // `relatedTarget` real.
      const relatedTarget = event.relatedTarget as Node | null
      if (relatedTarget && !container?.contains(relatedTarget)) close(false)
    }

    document.addEventListener('mousedown', handleOutside)
    container?.addEventListener('focusout', handleFocusOut)
    return () => {
      document.removeEventListener('mousedown', handleOutside)
      container?.removeEventListener('focusout', handleFocusOut)
    }
  }, [isOpen])

  function open() {
    setIsOpen(true)
  }

  function close(returnFocus: boolean) {
    setIsOpen(false)
    if (returnFocus) triggerRef.current?.focus()
  }

  function handlePanelKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault()
      close(true)
    }
  }

  return (
    <div
      ref={containerRef}
      className={cx(styles.field, isOpen && styles.active, rangeField.disabled && styles.disabled)}
      data-variant={variant === 'inverse' ? 'inverse' : undefined}
    >
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        onClick={() => {
          if (rangeField.disabled) return
          if (isOpen) close(false)
          else open()
        }}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={isOpen ? panelId : undefined}
        aria-label={isDefaultRange ? rangeField.label : `${rangeField.label}: ${displayText}`}
        disabled={rangeField.disabled}
      >
        <span className={cx(styles.label, (!isDefaultRange || isOpen) && styles.labelElevated)}>
          {rangeField.label}
        </span>
        {displayText && <span className={styles.value}>{displayText}</span>}
        <span className={styles.chevron} aria-hidden="true">
          {isOpen ? <IconChevUp size={24} /> : <IconChevDown size={24} />}
        </span>
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          id={panelId}
          role="group"
          aria-label={rangeField.label}
          className={styles.dropdown}
          onKeyDown={handlePanelKeyDown}
        >
          <RangeSliderField
            label={rangeField.label}
            min={rangeField.min}
            max={rangeField.max}
            value={rangeField.value}
            disabled={rangeField.disabled || rangeField.sliderDisabled}
            onChangeEnd={rangeField.onChangeEnd}
            formatValue={rangeField.formatValue}
            variant={variant}
          />
          {rangeField.extra?.({ variant })}
        </div>
      )}
    </div>
  )
}
